<template>
    <!-- heatmap -->

    <div>
        <l-surveys-insights-filters
            :survey="survey"
            :allowed-filters="['period', 'question', 'category']"
            :show-group-by1="true"
            :show-group-by2="true"
            :allowed-group-by1s="
                [
                    {
                        value: 'question',
                        text: $t('surveys.details.labelAllQuestions'),
                    },
                    {
                        value: 'category',
                        text: $t('surveys.details.labelAllTopics'),
                    },
                    hasMixedScales
                        ? null
                        : {
                              value: '',
                              text: $t('surveys.details.labelTotalScore'),
                          },
                ].filter(Boolean)
            "
            @change="onFilterChange"
        ></l-surveys-insights-filters>

        <div v-if="!items.length" class="text-center">
            <hr />
            <img alt="" :src="noDataIcon" class="placeholder-list" />
            <h4 class="color-grey">
                <span>{{ $t("surveys.details.labelNoData") }} </span>
            </h4>
        </div>

        <!-- End no results message -->

        <div v-else class="row" style="margin-top: 30px">
            <div ref="table" class="col-md-12">
                <!-- heatmap columns -->
                <b-table
                    id="heatmapv2"
                    :busy="isLoading"
                    primary-key="first_column"
                    responsive
                    sticky-header="80vh"
                    no-border-collapse
                    striped
                    hover
                    :tbody-transition-props="{ name: 'flip-list' }"
                    :items="items"
                    :fields="fields"
                >
                    <!-- A custom formatted header cell -->
                    <template #head()="data">
                        <div
                            style="min-width: 60px; display: flex; justify-content: center; align-items: center; flex-direction: column;"
                        >
                            <div
                                v-if="data.label"
                                v-b-popover.hover.top="{ content: data.label }"
                                class="ellipse-text"
                                style="width: 75px;"
                            >
                                {{ data.label }}
                            </div>
                            <div v-if="data.field && data.field.cnt">
                                <em>n = {{ data.field.cnt }}</em>
                            </div>
                        </div>
                    </template>

                    <!-- first columns: category or question -->
                    <template #cell(first_column)="data">
                        <b>{{ data ? data.value : "" }}</b>
                    </template>
                    <!-- data column -->
                    <template #cell()="data">
                        <strong>{{
                            data.value && data.value.avg_amt ? data.value.avg_amt : "n/a"
                        }}</strong>
                        <span
                            v-if="data.value && data.value.avg_amt && data.item.scaleType"
                            class="tw-ml-1 tw-text-xs"
                        >
                            /
                            {{ data.item.scaleType !== "nps" && data.item.scaleType ? 5 : 10 }}
                        </span>
                        <br />
                        <span v-if="data.value" style="font-size: 9px">
                            <i v-if="data.value.std_amt != undefined" class="fal fa-arrows-v"></i>
                            {{ data.value.std_amt != undefined ? data.value.std_amt : "" }}
                            <i
                                v-if="
                                    (!data.item.scaleType || data.item.scaleType === 'nps') &&
                                        data.value.nps != undefined
                                "
                                class="fal fa-thumbs-up"
                            ></i>
                            {{
                                (!data.item.scaleType || data.item.scaleType === "nps") &&
                                data.value.nps != undefined
                                    ? data.value.nps
                                    : ""
                            }}

                            <!-- show n/a if both are undefined-->
                            {{
                                data.value.std_amt == undefined && data.value.nps == undefined
                                    ? "n/a"
                                    : ""
                            }}
                        </span>
                        <span v-else style="font-size: 9px">n/a</span>
                    </template>
                </b-table>

                <div class="color-grey text-center" style="margin-top: 10px">
                    <i class="fal fa-arrows-v"></i> =
                    {{ $t("surveys.details.labelStandardDeviation") }}
                    <i class="fal fa-thumbs-up l-default-margin-s"></i> =
                    {{ $t("surveys.details.labelNPS") }}
                </div>

                <!-- end heatmap columns -->
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import icon from "@/img/icons/surveys.png";
import noDataIcon from "@/img/icons/nodata.png";
import html2canvas from "html2canvas";
import LSurveysInsightsFilters from "@/components/LSurveysInsightsFilters.vue";
import { BTable } from "bootstrap-vue";

export default {
    components: {
        LSurveysInsightsFilters,
        BTable,
    },
    props: {
        survey: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            error: false,
            filters: {},
            groupBy1: "",
            groupBy2: "",
            isLoading: true,
        };
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",
            user: "users/current/user",

            heatmapResult: "surveys/surveys/heatmapResult",
            benchmark: "surveys/surveys/benchmark",
        }),
        fields() {
            const renderColor = value => {
                if (!value.avg_amt) {
                    return "";
                }
                if (value.scaleType === "nps" || !value.scaleType) {
                    if (value.avg_amt < 5.5) {
                        return "dangerDark";
                    }
                    if (value.avg_amt < 7) {
                        return "danger";
                    }
                    if (value.avg_amt < 8) {
                        return "success50";
                    }
                    if (value.avg_amt < 9) {
                        return "success80";
                    }
                    return "success100";
                } else {
                    // 5-point scale
                    if (value.avg_amt < 1.25) {
                        return "dangerDark";
                    }
                    if (value.avg_amt < 2.5) {
                        return "danger";
                    }
                    if (value.avg_amt < 3) {
                        return "success70";
                    }
                    if (value.avg_amt < 4) {
                        return "success80";
                    }
                    return "success100";
                }
            };
            const columns = [
                {
                    label: " ",
                    key: "first_column",
                    sortable: true,
                    stickyColumn: true, // this will freeze this column
                    thClass: "surveySegmentTitle text-center",
                },
            ];
            columns.push(
                ...this.heatmapResult.columns.map(c => ({
                    ...c,
                    label: c.name,
                    key: `${c._id}`,
                    sortable: true,
                    class: "text-center minwidth heatmap-tile",
                    sortByFormatted: value => {
                        return value?.avg_amt; // sorting by avg_amt
                    },
                    thClass: "surveySegmentTitle text-center",
                    tdClass: renderColor,
                })),
            );

            return columns;
        },

        items() {
            const items = this.heatmapResult.data.map(item => ({
                ...Object.keys(item.data)
                    .map(key => ({
                        ...item.data[key],
                        key,
                        scaleType: item.scaleType,
                    }))
                    .reduce((acc, cur) => {
                        acc[cur.key] = cur;
                        return acc;
                    }, {}),
                first_column: item.name, // it could be question or category
                scaleType: item.scaleType,
            }));

            return items;
        },

        hasMixedScales() {
            const relevantQuestions = this.survey.questions.filter(question =>
                ["structured", "structuredOnly"].includes(question.skill?.answerType),
            );

            // contains both, 10 and 5-point scale
            return (
                relevantQuestions.some(question => question.scaleType !== "nps") &&
                relevantQuestions.some(question => question.scaleType === "nps")
            );
        },
    },
    watch: {
        heatmapResult: {
            immediate: true,
            handler(result) {
                this.$emit("change", result?.minObsAnonymity);
            },
        },
    },

    created() {
        this.icon = icon;
        this.noDataIcon = noDataIcon;
    },
    methods: {
        onFilterChange(type, { groupBy1, groupBy2, filters }) {
            this.isLoading = true;
            this.groupBy2 = groupBy2;
            this.groupBy1 = groupBy1;
            this.filters = filters;

            this.$store
                .dispatch("surveys/surveys/getHeatmapResult", {
                    payload: {
                        survey: this.$route.params.id,
                        groupBy1,
                        groupBy2,
                        filterBy: filters,
                        format: "json",
                    },
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        export() {
            const { groupBy1, groupBy2 } = this;
            this.$store.dispatch("surveys/surveys/exportHeatmap", {
                payload: {
                    survey: this.$route.params.id,
                    groupBy1,
                    groupBy2,
                    filterBy: this.filters,
                },
            });
        },

        exportPNG() {
            // scroll both vertically and horizontally to 0, 0
            this.$refs.table?.getElementsByClassName("b-table-sticky-header")?.[0].scrollTo(0, 0);
            html2canvas(document.getElementById("heatmapv2"), {
                scrollY: -window.scrollY, // make sure we have the whole table as the image
                scrollX: 0,
            }).then(function(canvas) {
                var image = canvas
                    .toDataURL("image/png", 1.0)
                    .replace("image/png", "image/octet-stream");

                var link = document.createElement("a");
                link.download = "heatmap.png";
                link.href = image;
                link.click();
            });
        },
        goTo(url) {
            this.$router.push(url);
        },
    },
};
</script>
<style>
table#heatmapv2 th:first-child {
    min-width: 300px;
}
table#heatmapv2 th {
    min-width: 100px;
    border: none;
    vertical-align: middle;
}

table#heatmapv2 .flip-list {
    transition: transform 1s;
}
</style>
<style scoped>
table {
    position: relative;
    border-collapse: collapse;
}

thead th {
    padding: 14px;
    line-height: 1.4;
    position: -webkit-sticky; /* for Safari */
    position: sticky;
    top: 0;
    background-color: #fff;
}

thead th:first-child {
    left: 0;
    z-index: 1;
}

tbody th {
    /* the first column */
    position: -webkit-sticky; /* for Safari */
    position: sticky;
    left: 0;
    background-color: #fff;
    min-width: 30vw;
}
td span i:nth-child(1) {
    padding-right: 5px;
}
td span i:nth-child(2) {
    padding: 0 4px;
}
</style>
